import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminAwardsManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('nominations');
  const [nominations, setNominations] = useState([]);
  const [votes, setVotes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [configForm, setConfigForm] = useState({
    year: new Date().getFullYear(),
    nomination_start_date: '',
    nomination_end_date: '',
    voting_start_date: '',
    voting_end_date: '',
    announcement_date: ''
  });
  const [savingConfig, setSavingConfig] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const ADMIN_TOKEN = 'admin_secret_token_2024';

  const categories = [
    { value: 'rookie', label: 'Best TA Rookie' },
    { value: 'excellence', label: 'Best TA Excellence' },
    { value: 'emerging_leader', label: 'Best Emerging TA Leader' },
    { value: 'manager', label: 'Best TA Manager' },
    { value: 'senior_leader', label: 'Best TA Senior Leader' }
  ];

  useEffect(() => {
    fetchConfig();
    if (activeTab === 'nominations') {
      fetchNominations();
    } else if (activeTab === 'votes') {
      fetchVotes();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab, filterCategory, filterStatus]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/config`);
      const data = await response.json();
      if (data.success) {
        setConfig(data.config);
        setConfigForm({
          year: data.config.year,
          nomination_start_date: data.config.nomination_start_date,
          nomination_end_date: data.config.nomination_end_date,
          voting_start_date: data.config.voting_start_date,
          voting_end_date: data.config.voting_end_date,
          announcement_date: data.config.announcement_date
        });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleSaveConfig = async () => {
    const { year, ...dates } = configForm;
    if (Object.values(dates).some(d => !d)) {
      alert('Please fill in all dates');
      return;
    }
    setSavingConfig(true);
    try {
      const isExistingYear = config && config.year === Number(year);
      const url = isExistingYear
        ? `${BACKEND_URL}/api/awards/admin/config/${year}`
        : `${BACKEND_URL}/api/awards/admin/config`;
      const response = await fetch(url, {
        method: isExistingYear ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': ADMIN_TOKEN
        },
        body: JSON.stringify({ year: Number(year), ...dates })
      });
      const data = await response.json();
      if (data.success) {
        alert(data.message);
        fetchConfig();
      } else {
        alert('Failed to save configuration: ' + (data.detail || data.message));
      }
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error saving configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const fetchNominations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/admin/nominations`, {
        headers: { 'X-Admin-Token': ADMIN_TOKEN }
      });
      const data = await response.json();
      if (data.success) {
        let filtered = data.nominations || [];
        if (filterCategory !== 'all') {
          filtered = filtered.filter(n => n.category === filterCategory);
        }
        if (filterStatus === 'qualified') {
          filtered = filtered.filter(n => n.is_qualified);
        } else if (filterStatus === 'unqualified') {
          filtered = filtered.filter(n => !n.is_qualified);
        }
        setNominations(filtered);
      }
    } catch (error) {
      console.error('Error fetching nominations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVotes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/admin/votes`, {
        headers: { 'X-Admin-Token': ADMIN_TOKEN }
      });
      const data = await response.json();
      if (data.success) {
        setVotes(data.votes || []);
      }
    } catch (error) {
      console.error('Error fetching votes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/admin/analytics`, {
        headers: { 'X-Admin-Token': ADMIN_TOKEN }
      });
      const data = await response.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisqualifyVote = async (voteId) => {
    if (!window.confirm('Are you sure you want to disqualify this vote?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/admin/vote/${voteId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Token': ADMIN_TOKEN }
      });
      const data = await response.json();
      if (data.success) {
        alert('Vote disqualified successfully');
        fetchVotes();
      } else {
        alert('Failed to disqualify vote: ' + data.message);
      }
    } catch (error) {
      console.error('Error disqualifying vote:', error);
      alert('Error disqualifying vote');
    }
  };

  const handleDeleteNomination = async (nominationId) => {
    if (!window.confirm('Are you sure you want to delete this nomination permanently?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/admin/nomination/${nominationId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Token': ADMIN_TOKEN }
      });
      const data = await response.json();
      if (data.success) {
        alert('Nomination deleted successfully');
        fetchNominations();
      } else {
        alert('Failed to delete nomination: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting nomination:', error);
      alert('Error deleting nomination');
    }
  };

  const handleFinalizeWinners = async () => {
    if (!window.confirm('Are you sure you want to finalize winners? This action cannot be undone!')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/admin/finalize`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Token': ADMIN_TOKEN 
        },
        body: JSON.stringify({ year: config?.year || new Date().getFullYear() })
      });
      const data = await response.json();
      if (data.success) {
        alert(`Winners finalized! ${data.total_winners} winners selected across all categories.`);
        fetchAnalytics();
      } else {
        alert('Failed to finalize winners: ' + data.message);
      }
    } catch (error) {
      console.error('Error finalizing winners:', error);
      alert('Error finalizing winners');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (isQualified) => {
    return isQualified
      ? 'bg-green-100 text-green-700'
      : 'bg-yellow-100 text-yellow-700';
  };

  const getCategoryName = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const filteredNominations = nominations.filter(nom =>
    nom.nominee_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    nom.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white py-8 shadow-lg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Awards Management
              </h1>
              <p className="text-yellow-100">Manage nominations, votes, and winners</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-6 py-3 bg-white text-orange-600 rounded-lg hover:bg-yellow-50 transition font-semibold"
            >
              ← Back to Admin
            </button>
          </div>

          {/* Status Bar */}
          {config && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                <p className="text-yellow-100 text-sm">Status</p>
                <p className="text-xl font-bold">{config.status?.replace('_', ' ').toUpperCase()}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                <p className="text-yellow-100 text-sm">Nominations End</p>
                <p className="text-xl font-bold">{new Date(config.nomination_end_date).toLocaleDateString()}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                <p className="text-yellow-100 text-sm">Voting End</p>
                <p className="text-xl font-bold">{new Date(config.voting_end_date).toLocaleDateString()}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                <p className="text-yellow-100 text-sm">Announcement</p>
                <p className="text-xl font-bold">{new Date(config.announcement_date).toLocaleDateString()}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('nominations')}
              className={`py-4 px-2 border-b-2 font-semibold transition ${
                activeTab === 'nominations'
                  ? 'border-yellow-600 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Nominations
            </button>
            <button
              onClick={() => setActiveTab('votes')}
              className={`py-4 px-2 border-b-2 font-semibold transition ${
                activeTab === 'votes'
                  ? 'border-yellow-600 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Votes
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-2 border-b-2 font-semibold transition ${
                activeTab === 'analytics'
                  ? 'border-yellow-600 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Analytics & Winners
            </button>
            <button
              data-testid="awards-config-tab"
              onClick={() => setActiveTab('configuration')}
              className={`py-4 px-2 border-b-2 font-semibold transition ${
                activeTab === 'configuration'
                  ? 'border-yellow-600 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Configuration
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Nominations Tab */}
        {activeTab === 'nominations' && (
          <div>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or company..."
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="all">All Status</option>
                  <option value="qualified">Qualified Only</option>
                  <option value="unqualified">Unqualified Only</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
                <p className="mt-4 text-gray-600">Loading nominations...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredNominations.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-12 text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No nominations found</h3>
                    <p className="text-gray-500">Try adjusting your filters</p>
                  </div>
                ) : (
                  filteredNominations.map((nomination) => (
                    <div key={nomination.nomination_id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(nomination.is_qualified)}`}>
                              {nomination.is_qualified ? 'QUALIFIED' : 'NOT QUALIFIED'}
                            </span>
                            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                              {getCategoryName(nomination.category)}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{nomination.nominee_name}</h3>
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                            <div>
                              <span className="font-semibold">Company:</span> {nomination.company || 'N/A'}
                            </div>
                            <div>
                              <span className="font-semibold">LinkedIn:</span> <a href={nomination.linkedin_profile} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View</a>
                            </div>
                            <div>
                              <span className="font-semibold">Total Votes:</span> {nomination.total_votes || 0}
                            </div>
                            <div>
                              <span className="font-semibold">Submitted:</span> {formatDate(nomination.created_at)}
                            </div>
                          </div>
                          <div className="flex gap-6 text-sm">
                            <span className="text-blue-600">Candidates: {nomination.votes_by_type?.candidate || 0}</span>
                            <span className="text-purple-600">Peers: {nomination.votes_by_type?.peer || 0}</span>
                            <span className="text-green-600">Sr/Jr: {nomination.votes_by_type?.senior_junior || 0}</span>
                            <span className="text-orange-600">Managers: {nomination.votes_by_type?.manager || 0}</span>
                            <span className="text-pink-600">HR: {nomination.votes_by_type?.hr || 0}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => window.open(`/awards/nominee/${nomination.unique_url}`, '_blank')}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm font-semibold"
                          >
                            View Profile
                          </button>
                          <button
                            onClick={() => handleDeleteNomination(nomination.nomination_id)}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition text-sm font-semibold"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Votes Tab */}
        {activeTab === 'votes' && (
          <div>
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
                <p className="mt-4 text-gray-600">Loading votes...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {votes.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-12 text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No votes yet</h3>
                    <p className="text-gray-500">Votes will appear when voting period starts</p>
                  </div>
                ) : (
                  votes.map((vote) => (
                    <div key={vote.vote_id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${vote.is_valid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {vote.is_valid ? 'VALID' : 'SUSPICIOUS'}
                            </span>
                            <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
                              {vote.voter_type?.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-semibold">Voter:</span> {vote.voter_name}
                            </div>
                            <div>
                              <span className="font-semibold">Nominee:</span> {vote.nominee_name}
                            </div>
                            <div>
                              <span className="font-semibold">Category:</span> {getCategoryName(vote.category)}
                            </div>
                            <div>
                              <span className="font-semibold">Voted:</span> {formatDate(vote.created_at)}
                            </div>
                            {vote.ip_address && (
                              <div>
                                <span className="font-semibold">IP:</span> {vote.ip_address}
                              </div>
                            )}
                          </div>
                        </div>
                        {!vote.is_valid && (
                          <button
                            onClick={() => handleDisqualifyVote(vote.vote_id)}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition text-sm font-semibold ml-4"
                          >
                            Disqualify
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
                <p className="mt-4 text-gray-600">Loading analytics...</p>
              </div>
            ) : analytics ? (
              <div>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-600 font-semibold">Total Nominations</h3>
                      <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-4xl font-bold text-gray-900">{analytics.total_nominations || 0}</p>
                    <p className="text-sm text-gray-500 mt-2">{analytics.qualified_nominations || 0} qualified</p>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-600 font-semibold">Total Votes</h3>
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-4xl font-bold text-gray-900">{analytics.total_votes || 0}</p>
                    <p className="text-sm text-gray-500 mt-2">Across all nominations</p>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-600 font-semibold">Avg Votes/Nominee</h3>
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <p className="text-4xl font-bold text-gray-900">
                      {analytics.total_nominations > 0 ? Math.round(analytics.total_votes / analytics.total_nominations) : 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">Engagement rate</p>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-600 font-semibold">Total Winners</h3>
                      <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <p className="text-4xl font-bold text-gray-900">{analytics.total_winners || 0}</p>
                    <p className="text-sm text-gray-500 mt-2">Out of 45 possible</p>
                  </div>
                </div>

                {/* Finalize Button */}
                {config?.status === 'review' && analytics.total_winners === 0 && (
                  <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg shadow-xl p-8 text-center mb-8">
                    <h2 className="text-3xl font-bold text-white mb-4">Ready to Finalize Winners?</h2>
                    <p className="text-white mb-6">The system will automatically select top performers based on vote counts and qualification criteria</p>
                    <button
                      onClick={handleFinalizeWinners}
                      className="px-8 py-4 bg-white text-orange-600 rounded-lg hover:bg-yellow-50 transition font-bold text-lg shadow-lg"
                    >
                      Finalize Winners Now
                    </button>
                  </div>
                )}

                {/* Category Breakdown */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Category Breakdown</h3>
                  <div className="space-y-4">
                    {categories.map((cat) => {
                      const catData = analytics.by_category?.[cat.value] || {};
                      return (
                        <div key={cat.value} className="border-l-4 border-yellow-500 pl-4">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold text-gray-900">{cat.label}</h4>
                            <span className="text-sm text-gray-500">{catData.nominations || 0} nominations</span>
                          </div>
                          <div className="flex gap-6 text-sm text-gray-600">
                            <span>Total Votes: {catData.total_votes || 0}</span>
                            <span>Qualified: {catData.qualified || 0}</span>
                            <span>Winners: {catData.winners || 0}/10</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <p className="text-gray-600">No analytics data available</p>
              </div>
            )}
          </div>
        )}
        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <div className="bg-white rounded-lg shadow p-8 max-w-3xl">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Award Cycle Configuration</h3>
            <p className="text-gray-600 mb-6">Edit the dates for the current award cycle, or set a new year to create a new cycle.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Award Year</label>
                <input
                  data-testid="config-year-input"
                  type="number"
                  value={configForm.year}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, year: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div></div>
              {[
                { key: 'nomination_start_date', label: 'Nominations Open' },
                { key: 'nomination_end_date', label: 'Nominations Close' },
                { key: 'voting_start_date', label: 'Voting Opens' },
                { key: 'voting_end_date', label: 'Voting Closes' },
                { key: 'announcement_date', label: 'Winners Announcement' }
              ].map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{field.label}</label>
                  <input
                    data-testid={`config-${field.key}-input`}
                    type="date"
                    value={configForm[field.key]}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              ))}
            </div>
            <button
              data-testid="save-config-btn"
              onClick={handleSaveConfig}
              disabled={savingConfig}
              className="mt-8 px-8 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-bold hover:from-yellow-600 hover:to-orange-600 transition disabled:opacity-50"
            >
              {savingConfig ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAwardsManagement;
